import React, { useRef, useState } from "react";
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    useDroppable,
    pointerWithin,
    closestCorners,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
    horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/* =========================
   ID helpers
========================= */
const COL = "col";
const ITEM = "item";
const DROP = "drop";

const pref = (type, id) => `${type}:${String(id)}`;
const parse = (raw) => {
    const [type, ...rest] = String(raw).split(":");
    return { type, id: rest.join(":") };
};

/* =========================
   Default fallback views
========================= */
function DefaultItemView({ data }) {
    return (
        <div
            style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 10,
                borderRadius: 8,
                background: "#fff",
                border: "1px solid #ddd",
            }}
        >
            {data?.label ?? "Item"}
        </div>
    );
}

function DefaultOverlayCard({ title }) {
    return (
        <div
            style={{
                padding: 10,
                borderRadius: 8,
                background: "#fff",
                border: "1px solid #ddd",
                boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            }}
        >
            {title}
        </div>
    );
}

/* =========================
   Sortable Item
========================= */
function SortableItem({ item, columnId, disabled }) {
    const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
        id: pref(ITEM, item.id),
        data: { type: ITEM, columnId: String(columnId) },
        disabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.2 : 1,
        cursor: disabled ? "default" : "grab",

        width: "100%",
        display: "block",
        boxSizing: "border-box",
    };

    const content =
        typeof item.template === "function" ? item.template(item) : <DefaultItemView data={item} />;

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {/* ✅ این wrapper تضمین می‌کنه template هم 100% بشه */}
            <div style={{ width: "100%", display: "block" }}>{content}</div>
        </div>
    );
}

/* =========================
   DropZone
========================= */
function ColumnDropZone({ columnId, disabled }) {
    const { setNodeRef, isOver } = useDroppable({ id: pref(DROP, columnId), disabled });

    return (
        <div
            ref={setNodeRef}
            style={{
                minHeight: 1,
                marginTop: 8,
                borderRadius: 10,
                background: isOver ? "#e9f5ff" : "transparent",
                border: isOver ? "1px dashed #7abfff" : "1px dashed transparent",
                width: "100%",
                boxSizing: "border-box",
            }}
        />
    );
}

/* =========================
   Sortable Column
========================= */
function SortableColumn({
                            column,
                            columnDraggable,
                            itemDraggable,
                            disableItemsBecauseColDrag,
                            isItemDragging,
                            isColumnDragging,
                            columnClassName,
                            columnWrapperClassName,
                            columnWrapperStyle,
                            columnBodyClassName,
                            columnItemsClassName,
                            columnHeaderClassName,
                            showColumnDragHandle,
                            dragColumnFromHeader,
                        }) {
    const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({
        id: pref(COL, column.id),
        data: { type: COL, columnId: String(column.id) },
        disabled: !columnDraggable || isItemDragging,
    });

    // ✅ ستون باید عرض واقعی داشته باشه تا آیتم‌ها 100% معنی‌دار بشن
    const computedWrapperStyle = typeof columnWrapperStyle === "function"
        ? columnWrapperStyle(column)
        : columnWrapperStyle;
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.88 : 1,

        flex: "1 1 0",
        minWidth: 0,
        width: "100%",
        boxSizing: "border-box",
        ...(computedWrapperStyle || {}),
    };

    const handleProps = columnDraggable ? { ...attributes, ...listeners } : {};
    const computedClassName =
        (typeof columnClassName === "function" ? columnClassName(column) : columnClassName) ||
        column.className ||
        "";
    const computedWrapperClassName = typeof columnWrapperClassName === "function"
        ? columnWrapperClassName(column)
        : columnWrapperClassName;
    const computedBodyClassName = typeof columnBodyClassName === "function"
        ? columnBodyClassName(column)
        : columnBodyClassName;
    const computedItemsClassName = typeof columnItemsClassName === "function"
        ? columnItemsClassName(column)
        : columnItemsClassName;
    const computedHeaderClassName = typeof columnHeaderClassName === "function"
        ? columnHeaderClassName(column)
        : columnHeaderClassName;

    return (
        <div ref={setNodeRef} style={style} className={computedWrapperClassName || ""}>
            <div
                className={computedClassName}
                style={{
                    width: "100%",
                    boxSizing: "border-box",
                    borderRadius: 10,
                    background: "#f4f5f7",
                    boxShadow: isDragging ? "0 12px 28px rgba(0,0,0,0.12)" : "none",

                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                }}
            >
                <div
                    {...(columnDraggable && dragColumnFromHeader ? handleProps : {})}
                    className={computedHeaderClassName || ""}
                    style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}
                >
                    {columnDraggable && showColumnDragHandle !== false && (
                        <span{...handleProps} className="border-round cursor-move">⠿</span>
                    )}
                    <div style={{ fontWeight: 700, flex: 1, minWidth: 0 }}>{column.label}</div>


                </div>

                <div className={computedBodyClassName || ""} style={{ pointerEvents: isColumnDragging ? "none" : "auto" }}>
                    <SortableContext
                        items={(column.items || []).map((it) => pref(ITEM, it.id))}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className={computedItemsClassName || ""} style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "stretch" }}>
                            {(column.items || []).map((it) => (
                                <SortableItem
                                    key={it.id}
                                    item={it}
                                    columnId={column.id}
                                    disabled={!itemDraggable || disableItemsBecauseColDrag || it?.draggable === false}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </div>

                <ColumnDropZone
                    columnId={String(column.id)}
                    disabled={!itemDraggable || disableItemsBecauseColDrag || isColumnDragging}
                />

                {column.footer ? <div style={{ marginTop: 10 }}>{column.footer}</div> : null}
            </div>
        </div>
    );
}

/* =========================
   Main Controlled Component
========================= */
export function DndSortable({
                                columns,
                                draggable = true,
                                draggableColumns = true,
                                draggableItems = true,
                                columnClassName,
                                columnWrapperClassName,
                                columnWrapperStyle,
                                columnBodyClassName,
                                columnItemsClassName,
                                columnHeaderClassName,
                                showColumnDragHandle = true,
                                dragColumnFromHeader = false,
                                boardClassName,
                                boardStyle,
                                appendContent,
                                onChange,
                            }) {
    const [active, setActive] = useState(null);
    const dragStartRef = useRef(null);

    const findColumnIndexById = (colIdStr, cols) =>
        cols.findIndex((c) => String(c.id) === String(colIdStr));

    const findColumnIdByItemId = (pureItemId, cols) => {
        for (const c of cols) {
            const idx = (c.items || []).findIndex((x) => String(x.id) === String(pureItemId));
            if (idx !== -1) return String(c.id);
        }
        return null;
    };

    const findItemIndexInColumn = (pureItemId, pureColumnId, cols) => {
        const col = cols.find((c) => String(c.id) === String(pureColumnId));
        if (!col) return -1;
        return (col.items || []).findIndex((x) => String(x.id) === String(pureItemId));
    };

    const findItemById = (pureItemId, cols) => {
        for (const c of cols) {
            const found = (c.items || []).find((x) => String(x.id) === String(pureItemId));
            if (found) return found;
        }
        return null;
    };

    const isItemDragging = active?.type === ITEM;
    const isColumnDragging = active?.type === COL;
    const disableItemsBecauseColDrag = isColumnDragging;

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const collisionDetection = (args) => {
        if (!draggable) return closestCorners(args);
        if (active?.type === ITEM) {
            const hits = pointerWithin(args);
            return hits.length ? hits : closestCorners(args);
        }
        return closestCorners(args);
    };

    const emit = (nextCols, info) => {
        if (typeof onChange === "function") onChange(nextCols, info);
    };

    const canDragColumnsGlobal = draggable && draggableColumns;
    const canDragItemsGlobal = draggable && draggableItems;

    const onDragStart = ({ active: aRaw }) => {
        if (!draggable) return;

        const a = parse(aRaw.id);

        if (a.type === COL) {
            if (!canDragColumnsGlobal) return;
            const col = columns.find((c) => String(c.id) === String(a.id));
            if (col?.draggable === false) return;
        }

        if (a.type === ITEM) {
            if (!canDragItemsGlobal) return;
            const item = findItemById(a.id, columns);
            if (item?.draggable === false) return;
        }

        setActive({ type: a.type, id: a.id });

        if (a.type === ITEM) {
            const fromColumnId = findColumnIdByItemId(a.id, columns);
            const fromIndex = fromColumnId ? findItemIndexInColumn(a.id, fromColumnId, columns) : -1;
            dragStartRef.current = { type: "item", id: a.id, fromColumnId, fromIndex };
        } else if (a.type === COL) {
            const fromIndex = findColumnIndexById(a.id, columns);
            dragStartRef.current = { type: "column", id: a.id, fromIndex };
        } else {
            dragStartRef.current = null;
        }
    };

    const onDragEnd = ({ active: aRaw, over }) => {
        if (!draggable) return;

        const start = dragStartRef.current;
        dragStartRef.current = null;

        if (!over) {
            setActive(null);
            return;
        }

        const a = parse(aRaw.id);
        const o = parse(over.id);

        // ----- Column move -----
        if (a.type === COL) {
            if (!canDragColumnsGlobal) {
                setActive(null);
                return;
            }
            const activeCol = columns.find((c) => String(c.id) === String(a.id));
            if (activeCol?.draggable === false) {
                setActive(null);
                return;
            }

            let overColId = null;
            if (o.type === COL) overColId = o.id;
            else if (o.type === DROP) overColId = o.id;
            else if (o.type === ITEM) overColId = findColumnIdByItemId(o.id, columns);

            if (!overColId) {
                setActive(null);
                return;
            }

            const oldIndex = findColumnIndexById(a.id, columns);
            const newIndex = findColumnIndexById(overColId, columns);

            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                const nextCols = arrayMove([...columns], oldIndex, newIndex);
                emit(nextCols, {
                    active: { type: "column", id: a.id },
                    from: { columnId: a.id, index: start?.fromIndex ?? oldIndex },
                    to: { columnId: a.id, index: newIndex },
                });
            } else {
                emit(columns, {
                    active: { type: "column", id: a.id },
                    from: { columnId: a.id, index: start?.fromIndex ?? oldIndex },
                    to: { columnId: a.id, index: oldIndex },
                });
            }

            setActive(null);
            return;
        }

        // ----- Item move -----
        if (a.type === ITEM) {
            if (!canDragItemsGlobal) {
                setActive(null);
                return;
            }
            const activeItem = findItemById(a.id, columns);
            if (activeItem?.draggable === false) {
                setActive(null);
                return;
            }

            let toColId = null;
            if (o.type === ITEM) toColId = findColumnIdByItemId(o.id, columns);
            else if (o.type === DROP) toColId = String(o.id);
            else if (o.type === COL) toColId = String(o.id);

            const fromColId = start?.fromColumnId ?? findColumnIdByItemId(a.id, columns);
            const fromIndex = typeof start?.fromIndex === "number" ? start.fromIndex : -1;

            if (!fromColId || !toColId) {
                setActive(null);
                return;
            }

            const fromColIdx = findColumnIndexById(fromColId, columns);
            const toColIdx = findColumnIndexById(toColId, columns);
            if (fromColIdx === -1 || toColIdx === -1) {
                setActive(null);
                return;
            }

            const next = columns.map((c) => ({ ...c, items: [...(c.items || [])] }));

            const fromItems = next[fromColIdx].items;
            const movingIdx = fromItems.findIndex((x) => String(x.id) === String(a.id));
            if (movingIdx === -1) {
                setActive(null);
                return;
            }

            const [moving] = fromItems.splice(movingIdx, 1);
            const toItems = next[toColIdx].items;

            if (o.type === DROP || o.type === COL) {
                toItems.push(moving);
                emit(next, {
                    active: { type: "item", id: a.id },
                    from: { columnId: fromColId, index: fromIndex },
                    to: { columnId: toColId, index: toItems.length - 1 },
                });
            } else {
                const overIndex = toItems.findIndex((x) => String(x.id) === String(o.id));
                const insertAt = overIndex === -1 ? toItems.length : overIndex;
                toItems.splice(insertAt, 0, moving);

                emit(next, {
                    active: { type: "item", id: a.id },
                    from: { columnId: fromColId, index: fromIndex },
                    to: { columnId: toColId, index: insertAt },
                });
            }

            setActive(null);
            return;
        }

        setActive(null);
    };

    const onDragCancel = () => {
        dragStartRef.current = null;
        setActive(null);
    };

    const overlayItem = active?.type === ITEM ? findItemById(active.id, columns) : null;
    const overlayCol = active?.type === COL ? columns.find((c) => String(c.id) === String(active.id)) : null;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
        >
            <SortableContext items={columns.map((c) => pref(COL, c.id))} strategy={horizontalListSortingStrategy}>
                <div
                    className={boardClassName || ""}
                    style={{
                        display: "flex",
                        gap: 16,
                        padding: 20,
                        alignItems: "stretch",
                        width: "100%", // ✅ ستون‌ها عرض واقعی می‌گیرن
                        boxSizing: "border-box",
                        ...(boardStyle || {}),
                    }}
                >
                    {columns.map((col) => {
                        const columnDraggable = canDragColumnsGlobal && col?.draggable !== false;
                        const itemDraggable = canDragItemsGlobal;

                        return (
                            <SortableColumn
                                key={col.id}
                                column={col}
                                columnDraggable={columnDraggable}
                                itemDraggable={itemDraggable}
                                disableItemsBecauseColDrag={disableItemsBecauseColDrag}
                                isItemDragging={isItemDragging}
                                isColumnDragging={isColumnDragging}
                                columnClassName={columnClassName}
                                columnWrapperClassName={columnWrapperClassName}
                                columnWrapperStyle={columnWrapperStyle}
                                columnBodyClassName={columnBodyClassName}
                                columnItemsClassName={columnItemsClassName}
                                columnHeaderClassName={columnHeaderClassName}
                                showColumnDragHandle={showColumnDragHandle}
                                dragColumnFromHeader={dragColumnFromHeader}
                            />
                        );
                    })}
                    {appendContent || null}
                </div>
            </SortableContext>

            <DragOverlay>
                {overlayItem ? (
                    typeof overlayItem.template === "function" ? (
                        <div className="shadow-2 border-round" style={{cursor:"grabbing"}}>{
                            overlayItem.template(overlayItem)
                        }</div>
                    ) : (
                        <DefaultOverlayCard title={overlayItem.label} />
                    )
                ) : null}

                {overlayCol ? (
                    <div style={{ width: 260 }} >
                        <div
                            style={{
                                padding: 10,
                                borderRadius: 10,
                                background: "#f4f5f7",
                                boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
                                fontWeight: 700,
                            }}>
                            {overlayCol.label}
                            <div style={{ opacity: 0.6, fontSize: 12, marginTop: 8 }}>در حال جابه‌جایی ستون…</div>
                        </div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
